import type { IsoCountryCode } from "@/lib/countries";

export enum PropertyType {
  HOUSE = "HOUSE",
  CONDO_LOBBY = "CONDO_LOBBY",
  CONDO_UNIT = "CONDO_UNIT",
  OFFICE = "OFFICE",
  WAREHOUSE = "WAREHOUSE",
  RETAIL = "RETAIL",
}

export interface Location {
  lat: number;
  lng: number;
}

export interface CustomerContact {
  id: string;
  customerId: string;

  // Contact Info
  contactName: string;
  contactCompany?: string;
  contactPhone?: string;
  contactEmail?: string;

  // Address Fields
  addressLine1: string;
  addressLine2?: string;
  addressCity: string;
  addressProvince: string;
  addressCountry: string;
  addressPostalCode?: string;
  addressPlusCode?: string;

  // Location & Validation
  location: Location;

  // Google Address Validation Data
  isValidated: boolean;
  validatedAt?: string;
  validationProvider?: string;
  validationConfidence?: string;
  googlePlaceId?: string;
  googleData?: Record<string, any>;

  // Property Info
  propertyResidential: boolean;
  propertyType?: PropertyType;

  // User Metadata
  label?: string;
  pickupInstructions?: string;
  deliveryInstructions?: string;
  isDefault: boolean;
  isFavorite: boolean;

  createdAt: string;
  updatedAt: string;
}

export interface CreateContactInput {
  // Contact Info
  contactName: string;
  contactCompany?: string;
  contactPhone?: string;
  contactEmail?: string;

  // Address Fields
  addressLine1: string;
  addressLine2?: string;
  addressCity: string;
  addressProvince: string;
  addressCountry: IsoCountryCode;
  addressPostalCode?: string;
  addressPlusCode?: string;

  // Location & Validation (REQUIRED)
  location: Location;

  // Google Address Validation Data
  isValidated?: boolean;
  validatedAt?: string;
  validationProvider?: string;
  validationConfidence?: string;
  googlePlaceId?: string;
  googleData?: Record<string, any>;

  // Property Info
  propertyResidential?: boolean;
  propertyType?: PropertyType;

  // User Metadata
  label?: string;
  pickupInstructions?: string;
  deliveryInstructions?: string;
  isDefault?: boolean;
  isFavorite?: boolean;
}

export interface UpdateContactInput {
  // Contact Info
  contactName?: string;
  contactCompany?: string;
  contactPhone?: string;
  contactEmail?: string;

  // Address Fields
  addressLine1?: string;
  addressLine2?: string;
  addressCity?: string;
  addressProvince?: string;
  addressCountry?: IsoCountryCode;
  addressPostalCode?: string;
  addressPlusCode?: string;

  // Location & Validation
  location?: Location;

  // Google Address Validation Data
  isValidated?: boolean;
  validatedAt?: string;
  validationProvider?: string;
  validationConfidence?: string;
  googlePlaceId?: string;
  googleData?: Record<string, any>;

  // Property Info
  propertyResidential?: boolean;
  propertyType?: PropertyType;

  // User Metadata
  label?: string;
  pickupInstructions?: string;
  deliveryInstructions?: string;
  isFavorite?: boolean;
}

export interface ListContactsQuery {
  search?: string;
  isFavorite?: boolean;
  label?: string;
  limit?: number;
  offset?: number;
}

export interface ListContactsResponse {
  contacts: CustomerContact[];
  total: number;
  limit: number;
  offset: number;
}

export interface ParsedContactData {
  name?: string;
  company?: string;
  phone?: string;
  email?: string;
  address?: {
    street1?: string;
    street2?: string;
    city?: string;
    province?: string;
    postalCode?: string;
    country?: string;
  };
  label?: "Home" | "Office" | "Warehouse" | "Other";
  propertyType?: "Residential" | "Commercial";
  pickupInstructions?: string;
  deliveryInstructions?: string;
  packageDetails?: string;
}
